import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import logo from '../assets/logo.svg';

const CONTENT = {
  fr: {
    nav: { lang: "EN", cta: "Voir l'invisible" },
    title: "Contact",
    subtitle: "Une question, un retour, une idée ? Écris-nous.",
    email: {
      label: "Email",
      value: "contact@voyx.app"
    },
    channels: [
      { icon: "✉", title: "Email général", value: "contact@voyx.app", href: "mailto:contact@voyx.app" },
      { icon: "🔒", title: "Confidentialité", value: "privacy@voyx.app", href: "mailto:privacy@voyx.app" },
      { icon: "🐛", title: "Bug report", value: "GitHub Issues", href: "https://github.com/Music-Cognition-Lab/VoYXEngine/issues" }
    ],
    backHome: "Retour à l'accueil",
    footer: {
      copy: "© 2026 VOYX. Tous droits réservés.",
      links: [
        { label: "Accueil", path: "/" },
        { label: "À propos", path: "/about" },
        { label: "Confidentialité", path: "/privacy" }
      ]
    }
  },
  en: {
    nav: { lang: "FR", cta: "See the invisible" },
    title: "Contact",
    subtitle: "A question, feedback, or an idea? Write to us.",
    email: {
      label: "Email",
      value: "contact@voyx.app"
    },
    channels: [
      { icon: "✉", title: "General email", value: "contact@voyx.app", href: "mailto:contact@voyx.app" },
      { icon: "🔒", title: "Privacy", value: "privacy@voyx.app", href: "mailto:privacy@voyx.app" },
      { icon: "🐛", title: "Bug report", value: "GitHub Issues", href: "https://github.com/Music-Cognition-Lab/VoYXEngine/issues" }
    ],
    backHome: "Back to Home",
    footer: {
      copy: "© 2026 VOYX. All rights reserved.",
      links: [
        { label: "Home", path: "/" },
        { label: "About", path: "/about" },
        { label: "Privacy", path: "/privacy" }
      ]
    }
  }
};

const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
  </svg>
);

const BugIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m8 2 1.88 1.88"/><path d="M14.12 3.88 16 2"/><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/>
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"/>
    <path d="M12 20v-9"/><path d="M6.53 9C4.6 8.8 3 7.1 3 5"/><path d="M6 13H2"/><path d="M3 21c0-2.1 1.7-3.9 3.8-4"/>
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"/><path d="M22 13h-4"/><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"/>
  </svg>
);

const CHANNEL_ICONS = [<MailIcon />, <ShieldIcon />, <BugIcon />];

const Contact = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('voyx-lang') || 'fr');
  const t = CONTENT[lang];
  const toggleLang = () => setLang(prev => { const next = prev === 'fr' ? 'en' : 'fr'; localStorage.setItem('voyx-lang', next); return next; });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="landing-page">
      <nav>
        <div className="nav-inner">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src={logo} alt="VOYX" />
          </div>
          <div className="nav-links" />
          <div className="nav-right">
            <button className="nav-lang" onClick={toggleLang}>{t.nav.lang}</button>
            <button className="nav-cta" onClick={() => navigate('/login')}>{t.nav.cta}</button>
          </div>
        </div>
      </nav>

      <div className="privacy-page">
        <div className="container" style={{ maxWidth: '800px' }}>
          <h1>{t.title}</h1>
          <p className="privacy-updated" style={{ fontSize: '16px', letterSpacing: '0', textTransform: 'none', fontFamily: 'var(--font-body)', color: 'var(--text-60)', fontWeight: 300, lineHeight: 1.6, marginBottom: '48px' }}>
            {t.subtitle}
          </p>

          <div className="privacy-permissions" style={{ gridTemplateColumns: '1fr' }}>
            {t.channels.map((channel, i) => (
              <a
                key={i}
                href={channel.href}
                target={channel.href.startsWith('http') ? '_blank' : undefined}
                rel={channel.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="privacy-permission-card"
                style={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                <span className="privacy-permission-card__icon">{CHANNEL_ICONS[i]}</span>
                <h3>{channel.title}</h3>
                <p>{channel.value}</p>
              </a>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '60px', marginBottom: '20px' }}>
            <button className="hero-cta" onClick={() => navigate('/')}>
              {t.backHome}
            </button>
          </div>
        </div>
      </div>

      <footer>
        <div className="container footer-inner">
          <div className="footer-brand">
            <img src={logo} alt="VOYX" onClick={() => navigate('/')} />
            <span>{t.footer.copy}</span>
          </div>
          <div className="footer-links">
            {t.footer.links.map((link, i) => (
              <a key={i} onClick={() => navigate(link.path)}>{link.label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
